/**
 * @fileoverview Custom Hook for Dynamic Select Options
 *
 * This hook manages dynamic select options that depend on another field's value.
 * It handles API calls, loading states, option transformation, and dependent field resets.
 *
 * Common Use Cases:
 * - Loading entity fields based on selected entity
 * - Loading categories based on selected type
 * - Loading sub-items based on selected parent item
 *
 * @example
 * // Basic usage
 * const { options, loading, dependentValue } = useDynamicSelectOptions({
 *   dependsOnValue: form.getFieldValue('parentId'),
 *   apiRoute: '/api/items/{id}/children',
 *   condition: () => someState === 'active',
 * });
 *
 * @example
 * // With custom response mapping
 * const { options } = useDynamicSelectOptions({
 *   dependsOnValue: entityId,
 *   apiRoute: '/entity/fields/{id}',
 *   params: { select: 'id,name', filter: 'active eq true' },
 *   mapResponse: (fields) => fields.map(f => ({
 *     label: f.name,
 *     value: f.id,
 *   })),
 * });
 */

import { useState, useEffect } from 'react';
import request from '@/utils/infrastructure/communication/request';
import { toQuery } from '@/utils/infrastructure';

/**
 * Hook for managing dynamic select options based on another field's value
 *
 * @param {Object} config - Configuration object
 * @param {any} config.dependsOnValue - Current value of the field this selector depends on
 * @param {string} config.apiRoute - API route template with {id} placeholder (e.g., '/api/items/{id}/children')
 * @param {Object} [config.params={}] - Query parameters for the API call (will be converted to OData format)
 * @param {Function} [config.mapResponse] - Optional function to transform API response to options array
 * @param {Function} [config.condition] - Optional condition function to determine if API call should be made
 * @param {Object} [config.form] - Ant Design form instance (required if using fieldToReset)
 * @param {string} [config.fieldToReset] - Field name to reset when dependency value changes
 * @param {boolean} [config.enabled=true] - Whether the hook is enabled (useful for conditional loading)
 * @param {boolean} [config.lazy=false] - If true, options are only loaded when loadOptions() is called manually
 *
 * @returns {Object} Hook state
 * @returns {Array} returns.options - Array of options for the select component
 * @returns {boolean} returns.loading - Loading state during API call
 * @returns {any} returns.dependentValue - Extracted value from dependsOnValue (handles object extraction)
 * @returns {Error|null} returns.error - Error object if API call fails
 * @returns {Function} returns.loadOptions - Manual function to trigger options loading (useful for lazy loading)
 */
export const useDynamicSelectOptions = ({
  dependsOnValue,
  apiRoute,
  params = {},
  mapResponse,
  condition = () => true,
  form,
  fieldToReset,
  enabled = true,
  lazy = false,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Extract ID from object if needed (handles nomenclature selectors)
  const extractedValue = dependsOnValue?.value || dependsOnValue?.id || dependsOnValue;

  /**
   * Manual load function for lazy loading
   */
  const loadOptions = () => {
    // Don't load if disabled, condition not met, or no value
    if (!enabled || !condition() || !extractedValue) {
      return Promise.resolve([]);
    }

    setLoading(true);
    setError(null);

    // Replace {id} placeholder in apiRoute with actual value
    const url = apiRoute.replace('{id}', extractedValue);

    return request(url, {
      params: toQuery(params),
    })
      .then((response) => {
        // Handle different API response formats:
        // - OData: { value: [...] }
        // - Standard pagination: { items: [...], totalCount: N }
        // - Direct array: [...]
        const data = response?.value || response?.items || response || [];

        // Apply custom mapper if provided, otherwise use data as-is
        const mappedOptions = mapResponse ? mapResponse(data) : data;

        setOptions(mappedOptions);
        setError(null);
        setHasLoadedOnce(true);
        return mappedOptions;
      })
      .catch((err) => {
        console.error('useDynamicSelectOptions: Failed to load options', err);
        setOptions([]);
        setError(err);
        throw err;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    // Clear options and error when dependency changes
    setOptions([]);
    setError(null);
    setHasLoadedOnce(false);

    // If lazy mode, don't auto-load (wait for manual loadOptions call)
    if (lazy) {
      return;
    }

    // Auto-load in non-lazy mode
    loadOptions();
  }, [
    extractedValue,
    apiRoute,
    JSON.stringify(params), // Stringify to detect deep changes
    enabled,
    lazy,
    // Note: condition and mapResponse are intentionally not in deps to avoid unnecessary re-renders
  ]);

  // Reset dependent field when parent value changes
  useEffect(() => {
    if (form && fieldToReset && extractedValue) {
      form.resetFields([fieldToReset]);
    }
  }, [extractedValue, form, fieldToReset]);

  return {
    options,
    loading,
    dependentValue: extractedValue,
    error,
    loadOptions, // Manual load function for lazy loading
    hasLoadedOnce, // Whether options have been loaded at least once
  };
};

export default useDynamicSelectOptions;
